import React, { useState } from "react";
import { Button, Col, Input, Modal, Row, Select, Table, Tooltip } from "antd";
import style from "./index.module.scss";
import { ColumnProps } from "antd/lib/table/interface";
import {
    CopyListItem,
    CopyParams,
    copyQuestionnaire,
    getCopyQuestionnaireList,
    getQuestionCourseInfo
} from "service/question";
import Moment from "moment";
import useRequest from "../../../../../../utils/useRequest";
import usePagination from "../../../../../../utils/v2/usePagination";
import useSelection from "../../../../../../utils/v2/useSelection";
import Form from "rc-field-form";
import FieldWithErrorHaveLabel from "../../../../../../components/FieldWithErrorHaveLabel";
import useRequestFn from "../../../../../../utils/useRequestFn";

interface CloneModalProps {
    onOk: () => void;
    questionnaireId: string;
}

interface GetDataFormProps {
    courseId: string;
    search: string;
}

export default function QuestionCloneModal({
    onOk,
    questionnaireId,
    ...restProps
}: CloneModalProps) {
    const [form] = Form.useForm<GetDataFormProps>();

    const [courseInfoList, { loading: courseInfoLoading }] = useRequest(
        () => getQuestionCourseInfo(),
        {},
        []
    );
    const pagination = usePagination(20);
    const rowSelection = useSelection();

    const [searchParams, setSearchParams] = useState<CopyParams>({
        courseId: "",
        currPage: pagination.pagination.current,
        pageSize: pagination.pagination.pageSize,
        questionnaireId,
        search: "",
        sortType: ""
    });

    const [data, { loading }, reload] = useRequest(
        () => getCopyQuestionnaireList(searchParams),
        {},
        [searchParams]
    );

    async function handleSearch(formData: GetDataFormProps) {
        setSearchParams({
            ...searchParams,
            search: formData.search,
            courseId: formData.courseId,
            currPage: 1
        });
    }

    const [copyAction, { loading: copyLoading }] =
        useRequestFn(copyQuestionnaire);

    async function doCopy() {
        try {
            await copyAction(questionnaireId, rowSelection.selected.join(","));
            await onOk();
        } catch (e) {}
    }

    const columns: ColumnProps<CopyListItem>[] = [
        {
            title: "序号",
            dataIndex: "rowNo",
            align: "center",
            width: 70
        },
        {
            title: "问卷名称",
            dataIndex: "questionnaireName",
            align: "left",
            render: text => (
                <div className={style.column}>
                    <Tooltip placement="topLeft" title={text}>
                        {text}
                    </Tooltip>
                </div>
            )
        },
        {
            title: "课程信息",
            dataIndex: "courseNo",
            align: "left",
            render: (text, record) => (
                <div className={style.column}>
                    <Tooltip
                        placement="topLeft"
                        title={`[${text}-${record.courseSeq}]${record.courseName}`}>
                        [{text}-{record.courseSeq}]{record.courseName}
                    </Tooltip>
                </div>
            )
        },
        {
            title: "周次节信息",
            dataIndex: "weekTimeInfo",
            align: "left",
            render: text => (
                <div className={style.column}>
                    <Tooltip placement="topLeft" title={text}>
                        {text}
                    </Tooltip>
                </div>
            )
        },
        {
            title: "题数",
            dataIndex: "questionCount",
            align: "center",
            width: 80
        },
        {
            title: "创建时间",
            dataIndex: "createTime",
            align: "center",
            width: 160,
            sorter: true,
            render: text => (
                <div>{Moment(text).format("YY/MM/DD HH:mm:ss")}</div>
            )
        }
    ];

    return (
        <Modal
            title="复制问卷"
            width={1200}
            className={style.root}
            okButtonProps={{ loading: copyLoading || loading }}
            onOk={doCopy}
            {...restProps}>
            <div className={style.header}>
                <Form
                    form={form}
                    className={style.form}
                    onFinish={handleSearch}>
                    <Row>
                        <Col span={6}>
                            <FieldWithErrorHaveLabel
                                label="课程："
                                name="courseId">
                                <Select
                                    loading={courseInfoLoading}
                                    placeholder="请选择课程"
                                    className={style.multipleCheck}>
                                    {courseInfoList?.map(item => (
                                        <Select.Option key={item.id}>
                                            {item.courseNo}
                                            {item.courseSeq}
                                            {item.courseName}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </FieldWithErrorHaveLabel>
                        </Col>
                        <Col span={6}>
                            <FieldWithErrorHaveLabel
                                label="问卷："
                                name="search">
                                <Input placeholder="请输入问卷活动名称关键词" />
                            </FieldWithErrorHaveLabel>
                        </Col>
                        <Col span={6}>
                            <Button htmlType="submit" type="primary">
                                搜索
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </div>

            <div className={style.body}>
                <Table
                    loading={loading}
                    columns={columns}
                    rowKey="questionnaireId"
                    dataSource={data?.list}
                    rowSelection={rowSelection}
                    scroll={{ y: 412 }}
                    {...pagination.tableProps(data?.allCount)}
                />
            </div>
        </Modal>
    );
}
